import { isMobilePhone, required } from "@/utils/inputValidators";
import { StyledInputField } from "../styledInputField/styledInputField";
import { CountryOption, localizedPhoneComponentType } from "./localizedTypes";
import { useEffect, useState } from "react";
import { CountryCodeSelector } from "./countryCodeSelector/countryCodeSelector";
import { useForm, useWatch } from "react-hook-form";
import { PhoneLocale } from "validator";
import { getRegionLocaleOptions } from "./localizationUtils";

/**
 * LocalizedPhoneInput is a component that displays input box that accepts a phone number with
 * localized way of presenting it, including area anc country codes
 * @param locale    {PhoneLocale}  - current localization code
 * @param value     {string}       - current phone number if known
 * @param name     {string}       - identifier for the form
 * @param form      {string}       - form (?)
 * @param readOnly  {boolean}      - if the field is read only
 * @returns visual component to enter/display localized phone number
 */
const LocalizedPhoneInput = ({
  locale,
  form: externalForm,
  readOnly,
  name,
}: localizedPhoneComponentType) => {
  const showDropDown = locale !== "en-US" && locale !== "en-CA";
  const regionLocaleOptions = getRegionLocaleOptions(locale);
  const countryList: CountryOption[] = Object.entries(regionLocaleOptions).map(
    ([localeCode, data]) => ({
      id: localeCode,
      ...data,
    })
  );
  const initialLocale =
    countryList.find((country) => country.id === locale) || countryList[0];
  const [selectedLocale, setSelectedLocale] = useState(initialLocale);

  const internalForm = useForm({
    defaultValues: {
      countryCode: selectedLocale.countryCode,
      [name]: "",
    },
    mode: "all",
  });

  const form = externalForm || internalForm;
  const { setValue, getValues, control } = form;

  const phoneWatcher: string = useWatch({ name, control })?.replace(
    /[^0-9]/g,
    ""
  );

  useEffect(() => {
    const formattedNumber = getValues()["formattedPhoneNumber"];
    if (formattedNumber === "") {
      if (locale !== "en-US" && locale !== "en-CA") {
        (phoneWatcher || "").replace(/^0+/, "");
        setValue(
          "formattedPhoneNumber",
          `${selectedLocale.countryCode}${phoneWatcher}`
        );
      } else {
        setValue(
          "formattedPhoneNumber",
          `${selectedLocale.countryCode}${phoneWatcher}`
        );
      }
    }
  }, []);

  const handleLocaleChange = (newLocale: CountryOption) => {
    if (newLocale.id !== selectedLocale.id) {
      setSelectedLocale(newLocale);
      setValue("countryCode", newLocale?.countryCode, { shouldDirty: true });
      if (phoneWatcher.charAt(0) === "0") {
        setValue(
          "formattedPhoneNumber",
          `${selectedLocale.countryCode}${phoneWatcher.replace(/^0+/, "")}`
        );
      } else {
        setValue(
          "formattedPhoneNumber",
          `${selectedLocale.countryCode}${phoneWatcher}`
        );
      }
    }
  };

  useEffect(() => {
    setValue("countryCode", selectedLocale?.countryCode);
  }, [selectedLocale, setValue]);

  useEffect(() => {
    if (locale !== "en-US" && locale !== "en-CA") {
      (phoneWatcher || "").replace(/^0+/, "");
      setValue(
        "formattedPhoneNumber",
        `${selectedLocale.countryCode}${phoneWatcher}`
      );
    } else {
      setValue(
        "formattedPhoneNumber",
        `${selectedLocale.countryCode}${phoneWatcher}`
      );
    }
  }, [phoneWatcher]);

  return (
    <div
      className={
        showDropDown ? "flex gap-2 justify-center items-start" : "w-full"
      }
    >
      {showDropDown && (
        <CountryCodeSelector
          currentLocaleOption={selectedLocale}
          countryList={countryList}
          handleLocaleChange={handleLocaleChange}
        />
      )}

      <StyledInputField
        key="phone"
        debounceTime={0}
        label="Phone Number"
        name={name || "phoneNumber"}
        type="format"
        placeholder={
          selectedLocale.hasDifferentFormats
            ? selectedLocale.mobilePlaceholder
            : selectedLocale.placeholder
        }
        format={
          selectedLocale.hasDifferentFormats
            ? selectedLocale.mobileFormat
            : selectedLocale.format
        }
        form={externalForm}
        theme={{
          width: "100%",
        }}
        validator={{
          isMobilePhone: isMobilePhone(selectedLocale.id as PhoneLocale),
          required: required("Phone Number"),
        }}
        readOnly={readOnly}
      />
    </div>
  );
};

export default LocalizedPhoneInput;
